import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight } from "lucide-react";

const PILLAR_IMAGES = [
  "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?w=1400&q=85&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1400&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1400&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1400&q=80&auto=format&fit=crop",
];

const PillarsCarousel = ({ pillars }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", duration: 30 },
    [Autoplay({ delay: 6000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollTo = useCallback(
    (idx) => emblaApi && emblaApi.scrollTo(idx),
    [emblaApi]
  );
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  return (
    <div className="relative" data-testid="pillars-carousel">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {pillars.map((p, i) => (
            <div
              key={p.n}
              className="shrink-0 grow-0 basis-full"
              data-testid={`pillar-slide-${p.n}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
                <div className="lg:col-span-6">
                  <img
                    src={PILLAR_IMAGES[i % PILLAR_IMAGES.length]}
                    alt=""
                    className="w-full h-[320px] md:h-[460px] object-cover grayscale"
                    loading="lazy"
                    data-testid={`pillar-image-${p.n}`}
                  />
                </div>
                <div className="lg:col-span-6 flex flex-col justify-center">
                  <div className="font-montserrat font-semibold text-synergia-gold text-xl tracking-widest">
                    {p.n}
                  </div>
                  <h3 className="mt-5 font-montserrat font-semibold text-2xl md:text-3xl text-synergia-green leading-snug text-balance">
                    {p.title}
                  </h3>
                  <div className="h-[2px] w-14 bg-synergia-gold mt-8" />
                  <p className="mt-8 font-inter text-base md:text-lg leading-relaxed text-synergia-graphite/75 max-w-xl">
                    {p.text}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between gap-6">
        <div className="flex items-center gap-3" data-testid="pillars-dots">
          {pillars.map((p, i) => (
            <button
              key={p.n}
              type="button"
              aria-label={`Pilar ${p.n}`}
              onClick={() => scrollTo(i)}
              data-testid={`pillars-dot-${p.n}`}
              className={`h-[2px] transition-all duration-300 ${
                i === selectedIndex
                  ? "w-10 bg-synergia-green"
                  : "w-6 bg-synergia-graphite/25 hover:bg-synergia-graphite/50"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={scrollPrev}
            aria-label="Previous"
            className="w-11 h-11 border border-synergia-line flex items-center justify-center text-synergia-graphite hover:bg-synergia-green hover:text-white hover:border-synergia-green transition-colors"
            data-testid="pillars-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            aria-label="Next"
            className="w-11 h-11 border border-synergia-line flex items-center justify-center text-synergia-graphite hover:bg-synergia-green hover:text-white hover:border-synergia-green transition-colors"
            data-testid="pillars-next"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PillarsCarousel;
